import {
  mergeClasses,
  SearchBox,
  SearchBoxChangeEvent,
} from "@fluentui/react-components";
import {
  ArrowRight16Regular,
  ChatEmpty20Regular,
  History20Regular,
} from "@fluentui/react-icons";
import { getWorkspaceConversations } from "api";
import { ConstantValues } from "common/constants";
import { IConversation } from "models/IConversation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useStyles } from "./WorkspaceConversations.styles";
import {
  IWorkspaceConversationSummary,
  IWorkspaceConversationsProps,
} from "./WorkspaceConversations.types";

const isClaude = (model?: string): boolean =>
  !!model && /claude|anthropic|haiku|sonnet|opus/i.test(model);

const isOpenai = (model?: string): boolean =>
  !!model && /openai|gpt/i.test(model);

const formatModelName = (model: string): string => {
  const lower = model.toLowerCase();
  if (lower === "openai") return "GPT-4o";
  if (lower === "openai-4.1") return "GPT-4.1";
  if (lower === "gpt-5-mini") return "GPT-5 Mini";
  if (lower === "gpt-5.2" || lower === "gpt-5.2-chat-latest") return "GPT-5.2";
  return model
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

type TimeGroup = "TODAY" | "EARLIER THIS WEEK" | "LAST WEEK" | "OLDER";

const TIME_GROUP_ORDER: TimeGroup[] = [
  "TODAY",
  "EARLIER THIS WEEK",
  "LAST WEEK",
  "OLDER",
];

const getTimeGroup = (unixTimestamp?: number): TimeGroup => {
  if (!unixTimestamp) return "OLDER";
  const now = new Date();
  const date = new Date(unixTimestamp * 1000);
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );

  if (date >= startOfToday) return "TODAY";

  const dayOfWeek = now.getDay();
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(
    startOfToday.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1),
  );

  if (date >= startOfWeek) return "EARLIER THIS WEEK";

  const startOfLastWeek = new Date(startOfWeek);
  startOfLastWeek.setDate(startOfWeek.getDate() - 7);
  if (date >= startOfLastWeek) return "LAST WEEK";

  return "OLDER";
};

const groupConversations = (
  conversations: IWorkspaceConversationSummary[],
): { label: TimeGroup; items: IWorkspaceConversationSummary[] }[] => {
  const groups: Record<string, IWorkspaceConversationSummary[]> = {};

  for (const conv of conversations) {
    const label = getTimeGroup(conv.updatedAt);
    if (!groups[label]) groups[label] = [];
    groups[label].push(conv);
  }

  return TIME_GROUP_ORDER.filter((label) => groups[label]?.length).map(
    (label) => ({ label, items: groups[label] }),
  );
};

const formatShortTime = (unixTimestamp: number): string => {
  const now = new Date();
  const date = new Date(unixTimestamp * 1000);
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );

  if (date >= startOfToday) {
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }

  const dayOfWeek = now.getDay();
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(
    startOfToday.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1),
  );

  if (date >= startOfWeek) {
    return date.toLocaleDateString([], { weekday: "short" });
  }

  const startOfLastWeek = new Date(startOfWeek);
  startOfLastWeek.setDate(startOfWeek.getDate() - 7);
  if (date >= startOfLastWeek) {
    return date.toLocaleDateString([], { weekday: "short" });
  }

  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const PAGE_SIZE = 15;
const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 2;

export const WorkspaceConversations = ({
  workspaceId,
  workspaceName,
  onSelectConversation,
}: IWorkspaceConversationsProps): JSX.Element => {
  const styles = useStyles();
  const [searchText, setSearchText] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback(
    (_e: SearchBoxChangeEvent, data: { value: string }) => {
      const value = data.value;
      setSearchText(value);
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      const trimmed = value.trim();
      if (trimmed.length === 0) {
        setDebouncedSearchText(ConstantValues.EMPTY_STRING);
      } else if (trimmed.length >= MIN_SEARCH_LENGTH) {
        searchDebounceRef.current = setTimeout(() => {
          setDebouncedSearchText(trimmed);
        }, DEBOUNCE_DELAY);
      }
    },
    [],
  );

  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      ["workspace-conversations", workspaceId],
      ({ pageParam = 0 }) =>
        getWorkspaceConversations({
          workspaceId,
          limit: PAGE_SIZE,
          offset: pageParam,
        }),
      {
        enabled: Boolean(workspaceId),
        staleTime: 30_000,
        cacheTime: 300_000,
        refetchOnWindowFocus: false,
        getNextPageParam: (
          lastPage: IWorkspaceConversationSummary[],
          allPages,
        ) => {
          if (!lastPage || lastPage.length < PAGE_SIZE) return undefined;
          return allPages.reduce((sum, page) => sum + page.length, 0);
        },
      },
    );

  const allConversations = useMemo(() => data?.pages.flat() ?? [], [data]);

  const conversations = useMemo(() => {
    if (!debouncedSearchText) return allConversations;
    const lower = debouncedSearchText.toLowerCase();
    return allConversations.filter((c) =>
      c.title?.toLowerCase().includes(lower),
    );
  }, [allConversations, debouncedSearchText]);

  const grouped = useMemo(
    () => groupConversations(conversations),
    [conversations],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleCardClick = (conversation: IWorkspaceConversationSummary) => {
    onSelectConversation(conversation as unknown as IConversation);
  };

  const handleCardKeyDown = (
    e: React.KeyboardEvent,
    conversation: IWorkspaceConversationSummary,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelectConversation(conversation as unknown as IConversation);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <History20Regular className={styles.headerIcon} />
          <span className={styles.headerTitle}>Workspace conversations</span>
        </div>
        <SearchBox
          className={styles.searchBox}
          appearance="filled-lighter"
          size="small"
          placeholder="Search conversations"
          value={searchText}
          onChange={handleSearchChange}
          dismiss={searchText ? undefined : null}
          aria-label={`Search conversations in ${workspaceName}`}
        />
      </div>

      {isLoading &&
        Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className={styles.skeletonCard} aria-hidden="true">
            <div className={styles.skeletonIcon} />
            <div className={styles.skeletonContent}>
              <div className={styles.skeletonTitle} />
              <div className={styles.skeletonDesc} />
            </div>
          </div>
        ))}

      {!isLoading && conversations.length === 0 && (
        <div className={styles.emptyState}>
          <span>No conversations yet</span>
          <span>Start chatting to see your history here</span>
        </div>
      )}

      {!isLoading &&
        conversations.length > 0 &&
        grouped.map((group) => (
          <div key={group.label}>
            <div className={styles.groupLabel}>{group.label}</div>
            <div className={styles.listContainer}>
              {group.items.map((conversation) => {
                const claude = isClaude(conversation.lastModelUsed);
                const openai = isOpenai(conversation.lastModelUsed);

                const iconColorClass = claude
                  ? styles.chatIconClaude
                  : openai
                    ? styles.chatIconOpenai
                    : styles.chatIconDefault;

                const modelColorClass = claude
                  ? styles.modelIndicatorClaude
                  : openai
                    ? styles.modelIndicatorOpenai
                    : styles.modelIndicatorDefault;

                const dotColorClass = claude
                  ? styles.modelDotClaude
                  : openai
                    ? styles.modelDotOpenai
                    : styles.modelDotDefault;

                const metaParts: string[] = [];
                if (
                  conversation.messageCount != null &&
                  conversation.messageCount > 0
                ) {
                  metaParts.push(
                    `${conversation.messageCount} message${conversation.messageCount === 1 ? "" : "s"}`,
                  );
                }
                if (conversation.updatedAt) {
                  metaParts.push(formatShortTime(conversation.updatedAt));
                }

                return (
                  <div
                    key={conversation.id}
                    className={styles.conversationCard}
                    onClick={() => handleCardClick(conversation)}
                    onKeyDown={(e) => handleCardKeyDown(e, conversation)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open conversation: ${conversation.title}`}
                  >
                    <div
                      className={mergeClasses(styles.chatIcon, iconColorClass)}
                    >
                      <ChatEmpty20Regular />
                    </div>

                    <div className={styles.cardContent}>
                      <span className={styles.cardTitle}>
                        {conversation.title}
                      </span>
                      {conversation.lastMessageSnippet && (
                        <span className={styles.cardSnippet}>
                          {conversation.lastMessageSnippet}
                        </span>
                      )}
                    </div>

                    <div className={styles.cardMeta}>
                      {conversation.lastModelUsed && (
                        <span
                          className={mergeClasses(
                            styles.modelIndicator,
                            modelColorClass,
                          )}
                        >
                          <span
                            className={mergeClasses(
                              styles.modelDot,
                              dotColorClass,
                            )}
                          />
                          {formatModelName(conversation.lastModelUsed)}
                        </span>
                      )}
                      {metaParts.length > 0 && (
                        <span className={styles.metaSubtext}>
                          {metaParts.join(" \u00B7 ")}
                        </span>
                      )}
                    </div>

                    <ArrowRight16Regular className={styles.arrowIcon} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}

      {isFetchingNextPage && (
        <div className={styles.emptyState}>Loading more...</div>
      )}

      <div ref={sentinelRef} style={{ height: 1 }} />
    </div>
  );
};
